import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BitwitPrompt } from '../interfaces/bitwit/bitwit-prompt';
import { BitwitResponse } from '../interfaces/bitwit/bitwit-response';

export interface BitwitAiConfig {
  baseUrl: string;
  projectName?: string;
  deploymentName?: string;
  apiVersion: string;
  apiKey?: string;
  defaultTemperature: number;
  defaultMaxTokens: number;
  telemetryEnabled: boolean;
}

interface FoundryEnvelope {
  id?: string;
  created?: number;
  created_on?: string;
  response_id?: string;
  output?: {
    text?: string;
  };
  output_text?: string;
  response?: {
    output_text?: string;
  };
  choices?: Array<{
    message?: { role: string; content?: string };
    content?: Array<{ text?: string }>;
  }>;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class BitwitAiService {
  readonly config: BitwitAiConfig;

  constructor(private readonly http: HttpClient) {
    const runtimeConfig = environment.bitwitAi || {};
    this.config = {
      baseUrl: runtimeConfig.baseUrl || '',
      projectName: runtimeConfig.projectName,
      deploymentName: runtimeConfig.deploymentName,
      apiVersion: runtimeConfig.apiVersion || '2024-05-01-preview',
      apiKey: runtimeConfig.apiKey,
      defaultTemperature:
        runtimeConfig.defaultTemperature === undefined
          ? 0.2
          : runtimeConfig.defaultTemperature,
      defaultMaxTokens:
        runtimeConfig.defaultMaxTokens === undefined
          ? 800
          : runtimeConfig.defaultMaxTokens,
      telemetryEnabled:
        runtimeConfig.telemetryEnabled === undefined
          ? true
          : runtimeConfig.telemetryEnabled,
    };
  }

  invoke(prompt: BitwitPrompt): Observable<BitwitResponse> {
    if (!this.isConfigured()) {
      return of({
        id: 'bitwit-offline',
        createdOn: new Date().toISOString(),
        text:
          'BitWit UI is ready. Configure `bitwitAi` in environment.ts to connect to Microsoft Foundry AI.',
      });
    }

    const url = this.buildInvokeUrl();
    const headers = this.createHeaders();
    const payload = this.buildPayload(prompt);

    return this.http.post<FoundryEnvelope>(url, payload, { headers }).pipe(
      map((envelope) => this.mapToBitwitResponse(envelope)),
      catchError((error) => {
        const friendly = error?.error?.error?.message || error?.message || 'Unknown error';
        return throwError(
          () => new Error(`BitWit Microsoft Foundry call failed: ${friendly}`),
        );
      }),
    );
  }

  private isConfigured(): boolean {
    return Boolean(
      this.config &&
        this.config.baseUrl &&
        this.config.deploymentName &&
        this.config.apiVersion,
    );
  }

  private buildInvokeUrl(): string {
    const parts = [this.config.baseUrl.replace(/\/$/, '')];

    if (this.config.projectName) {
      parts.push(`projects/${this.config.projectName}`);
    }

    if (this.config.deploymentName) {
      parts.push(`deployments/${this.config.deploymentName}`);
    }

    return `${parts.join('/')}:invoke?api-version=${this.config.apiVersion}`;
  }

  private createHeaders(): HttpHeaders {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.config.apiKey) {
      headers['api-key'] = this.config.apiKey;
    }

    return new HttpHeaders(headers);
  }

  private buildPayload(prompt: BitwitPrompt): Record<string, unknown> {
    const systemContext = [
      `You are BitWit, a Halliburton digital assistant that understands Request To Authorize (RTA) workflows and drill bits.`,
      `Respond with concise, actionable insights and highlight any missing data that the engineer should supply.`,
      `Context requested: ${prompt.contextType.toUpperCase()}.`,
    ];

    if (prompt.rtaId) {
      systemContext.push(`Focus on RTA ${prompt.rtaId}.`);
    }

    if (prompt.bitSerial) {
      systemContext.push(`Consider bit serial ${prompt.bitSerial}.`);
    }

    return {
      input: {
        messages: [
          {
            role: 'system',
            content: systemContext.join(' '),
          },
          {
            role: 'user',
            content: prompt.prompt,
          },
        ],
        temperature: prompt.temperature,
        max_output_tokens: prompt.maxTokens,
        metadata: {
          contextType: prompt.contextType,
          includeTelemetry: prompt.includeTelemetry,
          ...(prompt.metadata || {}),
        },
        attachments: prompt.attachments,
      },
    };
  }

  private mapToBitwitResponse(envelope: FoundryEnvelope): BitwitResponse {
    const text = this.extractText(envelope);
    return {
      id: envelope.id || envelope.response_id || 'bitwit-response',
      createdOn: envelope.created_on || (envelope.created ? new Date(envelope.created * 1000).toISOString() : new Date().toISOString()),
      text,
      diagnostics: {
        tokensPrompt: envelope.usage?.prompt_tokens,
        tokensCompletion: envelope.usage?.completion_tokens,
      },
    };
  }

  private extractText(envelope: FoundryEnvelope): string {
    if (envelope.output?.text) {
      return envelope.output.text;
    }
    if (envelope.output_text) {
      return envelope.output_text;
    }
    if (envelope.response?.output_text) {
      return envelope.response.output_text;
    }
    const choice = envelope.choices?.[0];
    if (choice?.message?.content) {
      return choice.message.content;
    }
    const content = choice?.content;
    if (content && content.length > 0) {
      return content.map((c) => c.text).filter(Boolean).join('\n');
    }
    return 'BitWit did not return any content.';
  }
}
